import { defineStore } from 'pinia';
import useLocalStorage from '@/composables/useLocalStorage';

export type ColorScheme = 'system' | 'light' | 'dark';

const COLOR_SCHEMES: ColorScheme[] = ['system', 'light', 'dark'];
const STORAGE_KEY = 'colorScheme';

// The phone's scheme. On iOS it reports the picked one while that is forced (see
// AppearancePlugin.swift), which is fine as it is only read while following the phone.
const systemScheme = window.matchMedia('(prefers-color-scheme: dark)');

// Switches on the dark palette, see the dark-theme block in theme/variables.css. Done right
// away rather than in a watcher, so anything watching isDark reads the new palette.
const applyColorScheme = (dark: boolean) => {
  document.documentElement.classList.toggle('dark-theme', dark);
};

/**
 * The app's colour scheme, picked under Profile > Utseende. It follows the system by
 * default, or stays light or dark whatever the system is set to. Stored on the device,
 * so it survives logging out.
 */
export const useAppearanceStore = defineStore('appearanceStore', {
  state: () => ({
    colorScheme: 'system' as ColorScheme,
    systemDark: systemScheme.matches,
  }),
  getters: {
    isDark: (state) => state.colorScheme === 'dark' || (state.colorScheme === 'system' && state.systemDark),
  },
  actions: {
    // Runs before the app mounts, so the first paint is already in the stored scheme
    async loadColorScheme() {
      systemScheme.addEventListener('change', (event) => {
        this.systemDark = event.matches;
        applyColorScheme(this.isDark);
      });
      try {
        const storage = await useLocalStorage();
        const stored = await storage.get(STORAGE_KEY);
        if (COLOR_SCHEMES.includes(stored)) {
          this.colorScheme = stored;
        }
      } catch (e) {
        console.warn('Loading the colour scheme failed', e);
      }
      applyColorScheme(this.isDark);
    },
    async setColorScheme(colorScheme: ColorScheme) {
      this.colorScheme = colorScheme;
      applyColorScheme(this.isDark);
      const storage = await useLocalStorage();
      await storage.set(STORAGE_KEY, colorScheme);
    },
  },
});
